import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Plus, Trash2, ImageIcon, Loader2, LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LINE_COLORS = {
  regular: "#F071AD",
  maternity: "#60A5FA",
  classic: "#4ADE80",
  athletic: "#FB923C",
};

const EMPTY = {
  name: "", line: "regular", tagline: "", color: LINE_COLORS.regular,
  image: "", absorbency: "", count: 10, price_ghs: "", features: "",
};

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function loadProducts() {
    try {
      // Public endpoint — plain axios (no token needed)
      const r = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(r.data.products || []);
    } catch {
      toast.error("Could not load products");
    }
  }

  useEffect(() => { loadProducts(); }, []);

  async function onLogout() {
    await logout();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  }

  function update(k, v) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "line" && LINE_COLORS[v]) next.color = LINE_COLORS[v];
      return next;
    });
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(`/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("image", r.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.image || !form.price_ghs) {
      toast.error("Name, image and price are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        line: form.line,
        tagline: form.tagline,
        color: form.color,
        image: form.image,
        absorbency: form.absorbency,
        count: Number(form.count) || 10,
        price_ghs: Number(form.price_ghs),
        features: form.features
          ? form.features.split("\n").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      await api.post(`/products`, payload);
      toast.success("Product added to catalog");
      setForm(EMPTY);
      await loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Remove this product from the catalog?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product removed");
      await loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div data-testid="admin-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Catalog Admin</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl mt-3 tracking-tighter">Add a new product.</h1>
            <p className="mt-4 text-faytex-muted max-w-2xl">
              Upload a hi-res product photograph and the details below. New products appear immediately on the public Shop page.
            </p>
          </div>
          {user && user !== false && (
            <div data-testid="admin-user-panel" className="flex items-center gap-3 bg-white rounded-full border border-gray-100 shadow-soft px-4 py-2">
              <div className="w-8 h-8 rounded-full bg-faytex-pink text-white flex items-center justify-center text-xs font-semibold uppercase">
                {(user.email || "?")[0]}
              </div>
              <div className="text-sm">
                <p className="font-medium text-faytex-ink leading-none" data-testid="admin-user-email">{user.email}</p>
                <p className="text-[10px] uppercase tracking-widest text-faytex-muted mt-1">{user.role}</p>
              </div>
              <button
                onClick={() => navigate("/admin/inquiries")}
                data-testid="admin-open-inquiries"
                className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-faytex-pinkLight text-faytex-pinkHover hover:bg-faytex-pink hover:text-white px-3 py-1.5 text-xs font-semibold transition-colors"
              >
                Inquiries
              </button>
              <button
                onClick={onLogout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-faytex-ink px-3 py-1.5 text-xs font-semibold"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-5 gap-10">
        {/* FORM */}
        <form
          onSubmit={submit}
          data-testid="admin-product-form"
          className="lg:col-span-3 bg-white rounded-3xl shadow-soft border border-gray-100 p-6 md:p-8 space-y-5"
        >
          <h2 className="font-heading text-2xl">Product details</h2>

          {/* Upload */}
          <div>
            <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Product image *</label>
            <div className="mt-2 flex items-center gap-4">
              <div
                className="w-28 h-28 rounded-2xl border border-dashed border-gray-300 bg-faytex-pinkLight/30 flex items-center justify-center overflow-hidden"
                data-testid="admin-image-preview"
              >
                {form.image ? (
                  <img src={resolveImageUrl(form.image)} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-faytex-muted" />
                )}
              </div>
              <label className="inline-flex items-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Choose image"}
                <input
                  data-testid="admin-image-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
            <p className="mt-2 text-[11px] text-faytex-muted">PNG, JPG, WEBP or GIF. Max 5MB.</p>
          </div>

          <Field label="Product name *" testid="admin-name" value={form.name} onChange={(v) => update("name", v)} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Product line *</label>
              <select
                data-testid="admin-line"
                value={form.line}
                onChange={(e) => update("line", e.target.value)}
                className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
              >
                <option value="regular">Regular</option>
                <option value="maternity">Maternity</option>
                <option value="classic">Classic</option>
                <option value="athletic">Active / Athletic</option>
              </select>
            </div>
            <Field label="Price (GH₵) *" type="number" testid="admin-price" value={form.price_ghs} onChange={(v) => update("price_ghs", v)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Absorbency (e.g. Medium)" testid="admin-absorbency" value={form.absorbency} onChange={(v) => update("absorbency", v)} />
            <Field label="Pack count" type="number" testid="admin-count" value={form.count} onChange={(v) => update("count", v)} />
          </div>

          <Field label="Tagline" testid="admin-tagline" value={form.tagline} onChange={(v) => update("tagline", v)} />

          <div>
            <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Features (one per line)</label>
            <textarea
              data-testid="admin-features"
              value={form.features}
              onChange={(e) => update("features", e.target.value)}
              rows={4}
              placeholder={"Ultra-thin core\nSoft-touch top sheet\nWings & leak guards"}
              className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="admin-submit"
              disabled={saving || !form.image}
              className="inline-flex items-center gap-2 rounded-full bg-faytex-ink hover:bg-faytex-pink text-white px-7 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {saving ? "Saving…" : "Add product"}
            </button>
          </div>
        </form>

        {/* LIST */}
        <div className="lg:col-span-2">
          <h2 className="font-heading text-2xl mb-4">Catalog</h2>
          <div data-testid="admin-product-list" className="space-y-3">
            {products.length === 0 && (
              <p className="text-sm text-faytex-muted">No products yet.</p>
            )}
            {products.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-product-${p.id}`}
                className="flex items-center gap-4 bg-white rounded-2xl border border-gray-100 p-3 shadow-soft"
              >
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-faytex-pinkLight/40 flex-shrink-0">
                  <img src={resolveImageUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-lg truncate">{p.name}</p>
                  <p className="text-xs text-faytex-muted truncate">
                    {p.line} • GH₵ {p.price_ghs}
                  </p>
                </div>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`admin-remove-${p.id}`}
                  className="p-2 rounded-full text-faytex-muted hover:text-red-500 hover:bg-red-50"
                  aria-label={`Remove ${p.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
      />
    </div>
  );
}
